import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import type { CalendarEvent } from 'src/meetings/models';
import { meetingsQueryKey } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';

export type AddParticipantBody =
  | { participantType: 'CLIENT'; clientId: string }
  | { participantType: 'USER'; userId: string }
  | { participantType: 'EXTERNAL'; name: string; description: string };

function participantsBasePath(eventId: string): string {
  return `calendar-events/${eventId}/participants`;
}

export const useMeetingParticipants = (scope: MeetingScope) => {
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: meetingsQueryKey(scope) });

  const addParticipant = useMutation<
    { data: CalendarEvent },
    Error,
    { eventId: string; body: AddParticipantBody }
  >({
    mutationFn: ({ eventId, body }) => api.post<CalendarEvent>(participantsBasePath(eventId), body),
    onSuccess: invalidate,
  });

  const removeParticipant = useMutation<unknown, Error, { eventId: string; participantId: string }>(
    {
      mutationFn: ({ eventId, participantId }) =>
        api.delete(`${participantsBasePath(eventId)}/${participantId}`),
      onSuccess: invalidate,
    },
  );

  return { addParticipant, removeParticipant };
};
